'use client'

/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { UseQueryResult } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'

import type { BookDetails } from '@package/api'
import type { CarouselApi } from '@ui/web/components'
import type { RenderLink } from '@ui/web/utils'
import {
  Autoplay,
  Button,
  Carousel,
  CarouselContent,
  CarouselItem,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
} from '@ui/web/components'
import { BookRowLeftChevronIcon, BookRowRightChevronIcon, MyCollectionIcon, ReadBookIcon } from '@ui/web/icon'
import { useMediaQuery } from '@helper/web/hooks'
import cn from '@config/tailwind/cn'

import { PutBooksInMyCollectionForm } from '../forms'
import { BookModal } from './book-modal'
import { LoveBookButton } from './love-book-button'

interface BaseBookRowProps {
  className?: string
  renderCollectionLink: RenderLink
}

interface BookRowWithChildrenProps extends BaseBookRowProps {
  children: React.ReactNode
  extraKey?: string
  renderReadBookLink?: never
  renderLoveBookLink?: never
  query?: never
  data?: never
}

interface BookRowWithQueryProps extends BaseBookRowProps {
  children?: never
  extraKey?: string
  /**
   * Query result that resolves to a list of books.
   */
  query: UseQueryResult<BookDetails[], Error>
  renderReadBookLink: RenderLink
  renderLoveBookLink: RenderLink
  data?: never
}

interface BookRowWithDataProps extends BaseBookRowProps {
  children?: never
  extraKey?: string
  query?: never
  /**
   * Direct array of book details to display.
   */
  data: BookDetails[]
  renderReadBookLink: RenderLink
  renderLoveBookLink: RenderLink
}

type BookRowProps = BookRowWithChildrenProps | BookRowWithQueryProps | BookRowWithDataProps

export function BookRow({
  className,
  children,
  extraKey,
  renderReadBookLink,
  renderLoveBookLink,
  renderCollectionLink,
  query,
  data,
}: BookRowProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [openPutBooksInMyCollectionDialog, setOpenPutBooksInMyCollectionDialog] = useState(false)
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null)

  const is2Xl = useMediaQuery('(min-width: 1536px)', { defaultValue: false })

  const plugin = useRef(Autoplay({ delay: 4000 + Math.floor(Math.random() * 6001), stopOnInteraction: true }))

  const scrollPrev = useCallback(() => {
    if (api) {
      api.scrollPrev()
    }
  }, [api])

  const scrollNext = useCallback(() => {
    if (api) {
      api.scrollNext()
    }
  }, [api])

  const hasChildren = Boolean(children)
  const hasQuery = Boolean(query) && !hasChildren
  const hasData = Boolean(data) && !hasChildren

  const queryData = hasQuery ? query!.data : undefined
  const isQueryPending = hasQuery ? query!.isPending : false
  const isQueryError = hasQuery ? query!.isError : false
  const isQueryRefetching = hasQuery ? query!.isRefetching : false

  const baseData = hasChildren ? undefined : hasData ? data! : (queryData ?? [])

  const carouselData: BookDetails[] = (() => {
    if (!baseData || baseData.length === 0) return []

    if (is2Xl && baseData.length < 20) {
      return [...baseData, ...baseData]
    }

    return baseData
  })()

  if (hasQuery && isQueryPending) {
    return <BookRowLoading className={className} />
  }

  if (hasQuery && isQueryError) {
    return <BookRowError className={className} onRetry={() => void query?.refetch()} isRefetching={isQueryRefetching} />
  }

  if (!hasChildren && carouselData.length === 0) {
    return null
  }

  return (
    <>
      <div className={cn('srd:w-full srd:mx-auto srd:relative', className)}>
        <Carousel
          plugins={[plugin.current]}
          setApi={setApi}
          className='srd:w-full'
          opts={{
            align: 'center',
            loop: true,
            slidesToScroll: 1,
          }}
        >
          <CarouselContent className='srd:-ml-2'>
            {(() => {
              if (children) return children

              return carouselData.map((book, index) => (
                <CarouselItem
                  key={`${book.id}-${index}-${extraKey}`}
                  className='srd:pl-2 srd:basis-1/2 srd:sm:basis-1/3 srd:md:basis-1/4 srd:lg:basis-[22%] srd:xl:basis-[18%] srd:2xl:basis-[14%] srd:min-[1920px]:basis-[12%]'
                >
                  <BookModal data={book} className='srd:w-full'>
                    <div className='srd:flex srd:flex-col srd:items-start srd:gap-y-4'>
                      <Button className='srd:w-full' asChild>
                        {renderReadBookLink!({
                          children: (
                            <>
                              <ReadBookIcon className='srd:size-6 srd:mr-2 srd:shrink-0' />
                              Baca buku ini
                            </>
                          ),
                          meta: { bookId: book.id, bookSlug: book.slug },
                        })}
                      </Button>

                      <div className='srd:w-full srd:flex srd:items-start srd:gap-x-4'>
                        <LoveBookButton className='srd:w-full' data={book} renderLinkLovedBooks={renderLoveBookLink!} />

                        <Button
                          variant='outline'
                          className='srd:w-full'
                          onClick={() => {
                            setSelectedBookId(book.id)
                            setOpenPutBooksInMyCollectionDialog(true)
                          }}
                        >
                          <MyCollectionIcon className='srd:size-6 srd:mr-2 srd:shrink-0' />
                          Masukkan ke koleksi
                        </Button>
                      </div>
                    </div>
                  </BookModal>
                </CarouselItem>
              ))
            })()}
          </CarouselContent>
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className='srd:absolute srd:top-1/2 srd:left-8 srd:-translate-y-1/2 srd:-translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:border-none srd:hover:bg-none srd:hover:bg-transparent'
              onClick={scrollPrev}
            >
              <BookRowLeftChevronIcon />
              <span className='srd:sr-only'>Buku sebelumnya</span>
            </motion.div>
          </AnimatePresence>
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Button
                variant='outline'
                size='icon'
                className='srd:absolute srd:top-1/2 srd:right-8 srd:-translate-y-1/2 srd:translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:border-none srd:hover:bg-none srd:hover:bg-transparent'
                onClick={scrollNext}
              >
                <BookRowRightChevronIcon />
                <span className='srd:sr-only'>Buku selanjutnya</span>
              </Button>
            </motion.div>
          </AnimatePresence>
        </Carousel>
      </div>

      <Dialog
        open={openPutBooksInMyCollectionDialog}
        onOpenChange={(open) => {
          setOpenPutBooksInMyCollectionDialog(open)
          if (!open) {
            setSelectedBookId(null)
          }
        }}
      >
        <DialogContent className='srd:w-full srd:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle>Tambahkan ke Kompilasiku</DialogTitle>
            <DialogDescription>Tambahkan buku ini ke kompilasiku.</DialogDescription>
          </DialogHeader>
          {selectedBookId && (
            <PutBooksInMyCollectionForm
              bookId={selectedBookId}
              onSuccess={() => setOpenPutBooksInMyCollectionDialog(false)}
              onCancel={() => setOpenPutBooksInMyCollectionDialog(false)}
              renderCollectionLink={renderCollectionLink}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

interface BookRowLoadingProps {
  className?: string
}

export function BookRowLoading({ className }: BookRowLoadingProps) {
  const [api, setApi] = useState<CarouselApi>()
  const is2Xl = useMediaQuery('(min-width: 1536px)', { defaultValue: false })
  const plugin = useRef(Autoplay({ delay: 4000 + Math.floor(Math.random() * 6001), stopOnInteraction: true }))

  const scrollPrev = useCallback(() => {
    if (api) {
      api.scrollPrev()
    }
  }, [api])

  const scrollNext = useCallback(() => {
    if (api) {
      api.scrollNext()
    }
  }, [api])

  const carouselData = is2Xl ? new Array(24).fill(0) : new Array(12).fill(0)

  return (
    <div className={cn('srd:w-full srd:mx-auto srd:relative', className)}>
      <Carousel
        plugins={[plugin.current]}
        setApi={setApi}
        className='srd:w-full'
        opts={{
          align: 'center',
          loop: true,
          slidesToScroll: 1,
        }}
      >
        <CarouselContent className='srd:-ml-2'>
          {carouselData.map((_, index) => (
            <CarouselItem
              key={`book-loading-${index}`}
              className='srd:pl-2 srd:basis-1/2 srd:sm:basis-1/3 srd:md:basis-1/4 srd:lg:basis-[22%] srd:xl:basis-[18%] srd:2xl:basis-[14%] srd:min-[1920px]:basis-[12%]'
            >
              <motion.div
                initial={{ opacity: 0, x: 10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className='srd:w-full srd:h-full'
              >
                <Skeleton className='srd:w-full srd:h-[225px] srd:aspect-3/4 srd:rounded-md' />
              </motion.div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className='srd:absolute srd:top-1/2 srd:left-8 srd:-translate-y-1/2 srd:-translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:border-none srd:hover:bg-none srd:hover:bg-transparent'
            onClick={scrollPrev}
          >
            <BookRowLeftChevronIcon />
            <span className='srd:sr-only'>Buku sebelumnya</span>
          </motion.div>
        </AnimatePresence>
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Button
              variant='outline'
              size='icon'
              className='srd:absolute srd:top-1/2 srd:right-8 srd:-translate-y-1/2 srd:translate-x-1/2 srd:w-fit srd:h-fit srd:rounded-full srd:border-none srd:hover:bg-none srd:hover:bg-transparent'
              onClick={scrollNext}
            >
              <BookRowRightChevronIcon />
              <span className='srd:sr-only'>Buku selanjutnya</span>
            </Button>
          </motion.div>
        </AnimatePresence>
      </Carousel>
    </div>
  )
}

interface BookRowErrorProps {
  className?: string
  onRetry: () => void
  isRefetching: boolean
}

export function BookRowError({ className, onRetry, isRefetching }: BookRowErrorProps) {
  return (
    <div
      className={cn(
        'srd:w-full srd:mx-auto srd:rounded-none srd:bg-neutrals-n-20 srd:px-4 srd:py-6 srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-3',
        className,
      )}
    >
      <p className='srd:text-base srd:font-semibold srd:text-neutrals-black'>Oops, terjadi kesalahan</p>
      <p className='srd:text-sm srd:text-neutrals-black'>Gagal memuat daftar buku. Silakan coba lagi.</p>
      <Button type='button' onClick={onRetry} disabled={isRefetching} className='srd:px-6 srd:py-2 srd:rounded-full'>
        {isRefetching ? 'Memuat...' : 'Coba Lagi'}
      </Button>
    </div>
  )
}
